import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { exportStyledExcel } from '../../../utils/excelExport';

// const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
const fmtDate = (value) => {
  return value || "-";
};
const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const SalePriceList = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate] = useState(fmt2Date(today));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);
  const [search, setSearch] = useState('');

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      console.log('Fetching:', `SaleReportApi/SalePriceListDetails/${companyId}/${branchId}/${fromDate}/${toDate}`);
      const res = await fetchData(
        'SaleReportApi',
        `/SalePriceListDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch sale price list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
  }, [companyId, branchId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.ItemName || '').toLowerCase().includes(q) ||
      (r.BatchNumber || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'SalePriceList', 0);
    if (!filtered.length) return;
    const colHeaders = ['#', 'Item Name', 'Batch', 'Current Price', 'Current Date', 'Previous Price', 'Previous Date', 'Change'];
    const rows = filtered.map((r, i) => {
      const curr = parseFloat(r.CurrentPrice) || 0;
      const prev = parseFloat(r.PreviousPrice) || 0;
      const change = prev > 0 ? (((curr - prev) / prev) * 100).toFixed(2) + '%' : '-';
      return [i + 1, r.ItemName, r.BatchNumber || '-', curr, fmtDate(r.CurrentDate), prev, fmtDate(r.PreviousDate), change];
    });

    await exportStyledExcel({
      reportTitle: 'SALE PRICE LIST REPORT',
      sheetName: 'Sale Price List',
      fileName: `SalePriceList_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      headers: colHeaders,
      rows,
      columnWidths: [4, 30, 14, 14, 14, 14, 14, 10],
    });
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
  const td = { padding: '7px 10px', textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };
  const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

  const PRIMARY = '#1c2266';
  const PRIMARY_MID = '#3f51b5';
  const PRIMARY_LT = '#e8eaf6';
  const PRIMARY_MD = '#c5cae9';

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading sale price list..." />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip title="Back to Reports" arrow>
              <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                <ArrowBack sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px' }}>Sale Price List</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search item, batch..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment> }}
              sx={{
                '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.6)' } },
                '& input::placeholder': { color: 'rgba(255,255,255,0.5)' },
                width: 200
              }}
            />
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark' }} />
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={fetchReport} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                <Refresh sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Excel" arrow>
              <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                <GetApp sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 750 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Item Name</th>
                <th style={th}>Batch</th>
                <th style={{ ...th, textAlign: 'right' }}>Current Price</th>
                <th style={th}>Current Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Previous Price</th>
                <th style={th}>Previous Date</th>
                <th style={{ ...th, textAlign: 'center' }}>Change</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const curr = parseFloat(r.CurrentPrice) || 0;
                  const prev = parseFloat(r.PreviousPrice) || 0;
                  const diff = curr - prev;
                  const pct = prev > 0 ? ((diff / prev) * 100).toFixed(2) : null;
                  const up = diff > 0;
                  const down = diff < 0;

                  return (
                    <tr key={r.ItemId || i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 600, color: '#1e293b' }}>{r.ItemName}</td>
                      <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{r.BatchNumber || '-'}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: '#1e40af' }}>
                        ₹{curr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ ...td, color: '#64748b' }}>{fmtDate(r.CurrentDate)}</td>
                      <td style={{ ...tdR, color: '#64748b' }}>
                        {prev > 0 ? `₹${prev.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td style={{ ...td, color: '#64748b' }}>{fmtDate(r.PreviousDate)}</td>
                      <td style={tdC}>
                        {pct === null ? (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>New</span>
                        ) : diff === 0 ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: '#64748b', fontSize: 11 }}>
                            <TrendingFlat sx={{ fontSize: 14 }} /> No Change
                          </span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 600, color: up ? '#dc2626' : '#059669' }}>
                            {up ? <TrendingUp sx={{ fontSize: 14 }} /> : <TrendingDown sx={{ fontSize: 14 }} />}
                            {up ? '+' : ''}{pct}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalePriceList;



